import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { adminApi, authApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useOpCo } from '../context/OpCoContext'
import {
  Users as UsersIcon, ShieldCheck, ShieldAlert,
  Search, UserCog, MoreVertical, AlertTriangle, Check, Loader2,
  X, Send, ClipboardCheck, FolderCog, Eye, UserPlus, RotateCcw,
  Clock3, MailWarning, UserMinus, UserCheck2, ArrowUpDown,
} from 'lucide-react'
import SuperAdminTag from '../components/opco/SuperAdminTag'
import UserAvatar from '../components/UserAvatar'
import SectionizedUserTable, { SORT_OPTIONS } from '../components/SectionizedUserTable'
import { isReadOnlyUser, READ_ONLY_MESSAGE, READER_ROLE_ENABLED } from '../utils/readerAccess'

export const INVITATION_STATES = {
  active: {
    label: 'Active',
    icon: Check,
    className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  },
  pending: {
    label: 'Invite sent',
    icon: Send,
    className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  },
  delivery_failed: {
    label: 'Delivery failed',
    icon: MailWarning,
    className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  },
  revoked: {
    label: 'Invite revoked',
    icon: X,
    className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  },
  expired: {
    label: 'Invite expired',
    icon: Clock3,
    className: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  },
  deactivated: {
    label: 'Deactivated',
    icon: X,
    className: 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  },
  unknown: {
    label: 'Status unknown',
    icon: AlertTriangle,
    className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  },
}

export function getInvitationState(user) {
  if (Object.prototype.hasOwnProperty.call(user, 'invitation_state')) {
    const explicitState = String(user.invitation_state || '').trim().toLowerCase()
    return INVITATION_STATES[explicitState] ? explicitState : 'unknown'
  }
  if (!user.is_active) return user.last_login ? 'deactivated' : 'revoked'
  return user.last_login ? 'active' : 'pending'
}

// The states admin_api.provision_users will actually resend an invitation
// for (see backend/src/api/admin_api.py). invitation_state is never the
// literal string 'accepted' — an accepted, active member comes back as
// 'active' from _user_to_dict — so this can't be a check for 'accepted'.
const RESENDABLE_INVITATION_STATES = ['pending', 'delivery_failed', 'expired', 'revoked']

// True once an existing user in the Invite Users search has nothing left to
// resend: already active, or accepted-then-deactivated.
export function isInviteFullyResolved(user) {
  return Boolean(user.already_in_xtract) && !RESENDABLE_INVITATION_STATES.includes(user.invitation_state)
}

function getProviderDeliveryHint(user) {
  const raw = String(user.invitation_provider_delivery_status || '').trim()
  if (!raw) return ''
  const normalized = raw.toLowerCase()

  if (normalized === 'delivered') return 'Delivered to recipient mail server'
  if (normalized === 'outfordelivery') return 'Out for delivery'
  if (normalized === 'submitted') return 'Submitted to provider'
  return raw
}

export function InvitationStatusBadge({ user }) {
  const state = getInvitationState(user)
  const definition = INVITATION_STATES[state] || INVITATION_STATES.unknown
  const Icon = definition.icon
  const deliveryHint = getProviderDeliveryHint(user)
  const smtpCode = user.invitation_delivery_smtp_status_code || ''
  const enhancedSmtpCode = user.invitation_delivery_enhanced_smtp_status_code || ''
  const statusTitle = [deliveryHint, smtpCode, enhancedSmtpCode].filter(Boolean).join(' | ')

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${definition.className}`}
      title={statusTitle || undefined}
    >
      <Icon className="w-3 h-3" />
      <span>{definition.label}</span>
      {deliveryHint && <span className="text-[10px] opacity-80">({deliveryHint})</span>}
    </span>
  )
}

// ─── Confirmation Modal ───
function ConfirmModal({ open, title, message, confirmLabel, confirmVariant, onConfirm, onCancel, loading }) {
  if (!open) return null
  const btnClass = confirmVariant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-navy-800 hover:bg-navy-900 text-white'
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2.5 mt-6">
          <button onClick={onCancel} disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${btnClass}`}>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Role Change Modal ───
// canManageAdmins=false (OpCo workspaces on, not an OpCo admin): the Admin
// role is global, so it can't be granted or taken away here (the API says 403).
function RoleModal({ open, user, mode = 'member', onClose, onConfirm, loading, canManageAdmins = true }) {
  const [newRole, setNewRole] = useState('user')

  useEffect(() => {
    if (!open || !user) return
    if (!canManageAdmins) setNewRole(user.role === 'user' ? 'reviewer' : 'user')
    else setNewRole(user.role === 'admin' ? 'user' : (user.role === 'reader' ? 'user' : 'admin'))
  }, [open, user, canManageAdmins])

  if (!open || !user) return null
  const adminLocked = !canManageAdmins && user.role === 'admin'

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Change Role</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {mode === 'invite'
            ? (
              <>
                Update role for <strong className="text-gray-900 dark:text-gray-100">{user.username || user.email}</strong> and send a fresh invitation email.
              </>
            )
            : (
              <>
                Update role for <strong className="text-gray-900 dark:text-gray-100">{user.username || user.email}</strong>
              </>
            )}
        </p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          {([
            { value: 'admin', label: 'Admin', desc: 'Manage users, templates, and settings', icon: ShieldCheck },
            { value: 'template_manager', label: 'Template Manager', desc: 'Manage templates and assign approvers', icon: FolderCog },
            { value: 'reviewer', label: 'Approver', desc: 'Review, approve, and publish requests', icon: ClipboardCheck },
            { value: 'sender', label: 'Reviewer', desc: 'Send requests for review only', icon: Send },
            { value: 'user', label: 'User', desc: 'Process documents using assigned templates', icon: UsersIcon },
            ...(READER_ROLE_ENABLED ? [{ value: 'reader', label: 'Reader', desc: 'View-only access to results and extractions', icon: Eye }] : []),
          ]).filter(option => canManageAdmins || option.value !== 'admin').map(option => {
            const Icon = option.icon
            const isSelected = newRole === option.value
            const isCurrent = user.role === option.value
            return (
              <button key={option.value} disabled={isCurrent || adminLocked}
                onClick={() => setNewRole(option.value)}
                className={`relative p-4 rounded-lg border-2 text-left transition-all disabled:opacity-40 ${
                  isSelected && !isCurrent
                    ? 'border-navy-800 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}>
                <Icon className={`w-5 h-5 mb-2 ${isSelected && !isCurrent ? 'text-navy-800 dark:text-blue-400' : 'text-gray-400'}`} />
                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{option.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{option.desc}</div>
                {isCurrent && <span className="absolute top-2 right-2 text-[10px] font-medium text-gray-400 uppercase">Current</span>}
                {isSelected && !isCurrent && (
                  <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-navy-800 dark:text-blue-400" /></div>
                )}
              </button>
            )
          })}
        </div>

        {!canManageAdmins && (
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            {adminLocked
              ? 'This user is an Admin. Only an OpCo admin can change an Admin\'s role.'
              : 'Only an OpCo admin can grant the Admin role, because it applies to every OpCo.'}
          </p>
        )}

        <div className="flex justify-end gap-2.5 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">
            Cancel
          </button>
          <button onClick={() => onConfirm(user.id, newRole)} disabled={loading || adminLocked || newRole === user.role}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {mode === 'invite' ? 'Save & Send Invite' : 'Confirm Change'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Invite User Modal ───
// OpCo workspaces on: opcoName is the workspace every invitee joins (new
// people, and people already in Xtract who are not in it yet), and
// canGrantAdmin is false unless the caller is an OpCo admin. Also used by
// OpCo managers from the OpCos tab (components/opco/admin/MembersPanel.jsx).
export function AddUserModal({ open, onClose, onSuccess, canGrantAdmin = true, opcoName = null }) {
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [defaultRole, setDefaultRole] = useState('user')
  const [userRoles, setUserRoles] = useState({})
  const [provisioning, setProvisioning] = useState(false)
  const [message, setMessage] = useState('')
  const [hideExisting, setHideExisting] = useState(false)
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)
  const searchTimeoutRef = useRef(null)

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchQuery('')
      setSearchResults([])
      setSelectedUsers([])
      setDefaultRole('user')
      setUserRoles({})
      setMessage('')
      setHideExisting(false)
      setShowSelectedOnly(false)
    }
  }, [open])

  // Debounced search
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([])
      return
    }

    setSearching(true)
    setMessage('')
    try {
      const res = await adminApi.searchEntraUsers(query, 25)
      setSearchResults(res.data?.users || [])
      if (res.data?.graph_configured === false) {
        setMessage(res.data.message || 'Microsoft Graph is not configured')
      } else if ((res.data?.users || []).length === 0) {
        setMessage('No users found matching your search')
      }
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to search users')
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  // Debounce the search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(searchQuery)
    }, 300)
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, handleSearch])

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id)
      if (isSelected) {
        setUserRoles(current => {
          const next = { ...current }
          delete next[user.id]
          return next
        })
        return prev.filter(u => u.id !== user.id)
      }
      setUserRoles(current => ({
        ...current,
        [user.id]: current[user.id] || defaultRole,
      }))
      return [...prev, user]
    })
  }

  const handleRoleChangeForUser = (userId, role) => {
    setUserRoles(prev => ({ ...prev, [userId]: role }))
  }

  const applyRoleToAllSelected = (role = defaultRole) => {
    setUserRoles(prev => {
      const next = { ...prev }
      selectedUsers.forEach(user => {
        next[user.id] = role
      })
      return next
    })
  }

  const handleProvision = async () => {
    if (selectedUsers.length === 0) return

    setProvisioning(true)
    try {
      const usersToProvision = selectedUsers.map(u => ({
        entra_id: u.id,
        email: u.email,
        name: u.displayName,
        job_title: u.jobTitle,
        role: userRoles[u.id] || defaultRole,
      }))
      
      const res = await adminApi.provisionUsers(usersToProvision, defaultRole)
      const data = res.data

      if (data.total_provisioned > 0) {
        const submitted = data.total_invitation_emails_submitted ?? data.total_invitation_emails_sent ?? 0
        const failed = data.total_invitation_emails_failed || 0
        if (submitted > 0) {
          toast.success('Invite sent')
        }
        if (failed > 0) {
          toast.error(`${failed} invitation email${failed > 1 ? 's need' : ' needs'} attention. Use the Invitations filter to retry.`)
        }
      }
      if (data.total_skipped > 0) {
        const resent = (data.skipped || []).filter(s => s.invitation_email_submitted !== undefined)
        const resentSucceeded = resent.filter(s => s.invitation_email_submitted).length
        const resentFailed = resent.length - resentSucceeded
        const plainSkipped = data.total_skipped - resent.length

        if (resentSucceeded > 0) {
          toast.info(`${resentSucceeded} already existed — invitation email resent`)
        }
        if (resentFailed > 0) {
          toast.error(`${resentFailed} already existed and the resend failed. Use Resend Invite on their row.`)
        }
        if (plainSkipped > 0) {
          toast.info(`${plainSkipped} user${plainSkipped > 1 ? 's' : ''} already existed`)
        }
      }
      if (data.total_errors > 0) {
        const otherOpco = (data.errors || []).filter(e => e.code === 'OPCO_INVITEE_OTHER_OPCO').length
        if (otherOpco > 0) {
          toast.error(`${otherOpco} user${otherOpco > 1 ? 's belong' : ' belongs'} to another OpCo. Ask a Super Admin to add them.`)
        }
        if (data.total_errors > otherOpco) {
          const n = data.total_errors - otherOpco
          toast.error(`Failed to invite ${n} user${n > 1 ? 's' : ''}`)
        }
      }
      if (data.total_added_to_opco > 0) {
        const n = data.total_added_to_opco
        toast.success(`${n} existing user${n > 1 ? 's' : ''} added to ${opcoName || data.opco}`)
      }

      if (data.total_provisioned > 0 || data.total_added_to_opco > 0) {
        onSuccess?.()
      }
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to invite users')
    } finally {
      setProvisioning(false)
    }
  }

  if (!open) return null

  const roleOptions = [
    { value: 'admin', label: 'Admin', desc: 'Full access', icon: ShieldCheck },
    { value: 'template_manager', label: 'Template Manager', desc: 'Manage templates', icon: FolderCog },
    { value: 'reviewer', label: 'Approver', desc: 'Review & approve', icon: ClipboardCheck },
    { value: 'sender', label: 'Reviewer', desc: 'Send for review', icon: Send },
    { value: 'user', label: 'User', desc: 'Standard access', icon: UsersIcon },
    ...(READER_ROLE_ENABLED ? [{ value: 'reader', label: 'Reader', desc: 'View only', icon: Eye }] : []),
  ].filter(option => canGrantAdmin || option.value !== 'admin')

  // An existing user whose invite hasn't been accepted yet is still
  // actionable from this modal — selecting them resends the invite instead
  // of being blocked outright (see issue #423). With OpCo workspaces on, an
  // accepted user who is not in this OpCo yet is selectable too: inviting
  // them adds them to it (their role is left as it is).
  const joinsOpcoOnly = (user) => Boolean(opcoName) && isInviteFullyResolved(user) && user.in_current_opco === false
  const isAlreadyAccepted = (user) => isInviteFullyResolved(user) && !joinsOpcoOnly(user)

  const selectedCount = selectedUsers.length
  const selectedIds = new Set(selectedUsers.map((u) => u.id))
  const visibleResults = searchResults.filter((user) => {
    if (hideExisting && isAlreadyAccepted(user)) return false
    if (showSelectedOnly && !selectedIds.has(user.id)) return false
    return true
  })
  const selectedReadyCount = selectedUsers.filter((u) => !isAlreadyAccepted(u)).length
  const roleLabels = roleOptions.reduce((acc, role) => {
    acc[role.value] = role.label
    return acc
  }, {})
  const alreadyAddedCount = searchResults.filter(user => user.already_in_xtract).length

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto" style={{ width: '100vw', height: '100vh' }}>
      <div className="fixed inset-0 bg-gray-900/55 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative my-2 sm:my-0 w-full max-w-[1180px] h-[min(90vh,860px)] flex flex-col bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Invite Users</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Select users from your Entra directory and configure their access before first sign-in.
              {opcoName && <> Everyone you invite joins <strong className="text-gray-700 dark:text-gray-200">{opcoName}</strong>.</>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-white dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close invite modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
            <div className="relative min-w-0 lg:max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or username"
                className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                autoFocus
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <button
                onClick={() => setHideExisting((v) => !v)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  hideExisting
                    ? 'bg-navy-800 text-white border-navy-800'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                }`}
              >
                Hide Already Added
              </button>
              <button
                onClick={() => setShowSelectedOnly((v) => !v)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  showSelectedOnly
                    ? 'bg-navy-800 text-white border-navy-800'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                }`}
              >
                Show Selected Only
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/30 px-3 py-2.5">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Default role</span>
            <select
              value={defaultRole}
              onChange={(e) => setDefaultRole(e.target.value)}
              className="min-w-[170px] rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-navy-500/20"
            >
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              onClick={() => applyRoleToAllSelected(defaultRole)}
              disabled={selectedCount === 0}
              className="rounded-lg border border-navy-200 dark:border-blue-800 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-navy-800 dark:text-blue-300 hover:bg-navy-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Apply to selected
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="px-2.5 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
                {selectedCount} selected for invite
              </span>
              <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 font-medium">
                {alreadyAddedCount} already in workspace
              </span>
              <span className="px-2.5 py-1 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium">
                {selectedReadyCount} ready to invite
              </span>
            </div>
            <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <span>{visibleResults.length} results</span>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span>Preset role: {roleLabels[defaultRole] || 'User'}</span>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden bg-gray-50/40 dark:bg-gray-900/30">
          <div className="h-full flex flex-col px-4 py-3 gap-3">
          {message && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">{message}</p>
            </div>
          )}

          {!searching && visibleResults.length > 0 && (
            <div className="flex-1 min-h-0">
              <div className="h-full overflow-auto rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                <table className="w-full min-w-[900px] table-fixed">
                  <colgroup>
                    <col className="w-[64px]" />
                    <col className="w-[34%]" />
                    <col className="w-[30%]" />
                    <col className="w-[22%]" />
                    <col className="w-[14%]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur">
                      <th className="px-4 py-3 w-12 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Select</th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email / Title</th>
                      <th className="px-4 py-3 w-56 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 w-44 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {visibleResults.map((user) => {
                      const isSelected = selectedIds.has(user.id)
                      const alreadyExists = user.already_in_xtract
                      const alreadyAccepted = isAlreadyAccepted(user)
                      const addToOpcoOnly = joinsOpcoOnly(user)

                      return (
                        <tr
                          key={user.id}
                          className={`${
                            isSelected
                              ? 'bg-blue-50/60 dark:bg-blue-900/15'
                              : 'bg-white dark:bg-gray-900'
                          } hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}
                        >
                          <td className="px-4 py-3 align-middle">
                            <button
                              onClick={() => !alreadyAccepted && toggleUserSelection(user)}
                              disabled={alreadyAccepted}
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                                isSelected
                                  ? 'bg-navy-800 border-navy-800 dark:bg-blue-600 dark:border-blue-600'
                                  : 'border-gray-300 dark:border-gray-600'
                              } ${alreadyAccepted ? 'opacity-50 cursor-not-allowed' : 'hover:border-navy-500'}`}
                              aria-label={`Select ${user.displayName || user.email}`}
                            >
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </button>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <div className="flex items-center gap-3 min-w-0">
                              <UserAvatar
                                user={{
                                  id: user.id,
                                  username: user.displayName,
                                  email: user.email,
                                  photo_mode: 'entra',
                                  photo_id: user.id,
                                }}
                                size="md"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{user.displayName || user.email}</p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.jobTitle || 'No title available'}</p>
                                  {user.userType && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                                      {user.userType}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{user.email}</p>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <select
                              value={userRoles[user.id] || defaultRole}
                              onChange={(e) => handleRoleChangeForUser(user.id, e.target.value)}
                              disabled={!isSelected || alreadyAccepted || addToOpcoOnly}
                              className="w-full min-w-[180px] rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-navy-500/20"
                            >
                              {roleOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {alreadyAccepted ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                Already added
                              </span>
                            ) : addToOpcoOnly ? (
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${isSelected
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                                {isSelected ? `Will be added to ${opcoName}` : `In Xtract — select to add to ${opcoName}`}
                              </span>
                            ) : alreadyExists && isSelected ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                Invite will be resent
                              </span>
                            ) : alreadyExists ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                Invite pending — select to resend
                              </span>
                            ) : isSelected ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                Ready to invite
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                Not selected
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!searching && visibleResults.length === 0 && searchQuery.length >= 2 && !message && (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 text-center rounded-xl border border-dashed border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <UsersIcon className="w-10 h-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No matching users found</p>
            </div>
          )}

          {!searching && searchQuery.length < 2 && (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 text-center rounded-xl border border-dashed border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <Search className="w-10 h-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Type at least 2 characters to start searching</p>
            </div>
          )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Each selected user can have an individual role.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={provisioning}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleProvision}
              disabled={selectedCount === 0 || provisioning}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-navy-800 hover:bg-navy-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {provisioning && <Loader2 className="w-4 h-4 animate-spin" />}
              Invite {selectedCount > 0 ? selectedCount : ''} {selectedCount === 1 ? 'User' : 'Users'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── User Row Actions Menu ───
function UserActions({
  user,
  currentUser,
  onChangeRole,
  onChangeInviteRole,
  onDeactivate,
  onResendInvite,
  onRestoreInvite,
  onRevokeInvite,
  onReactivate,
  readOnly,
}) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const invitationState = getInvitationState(user)

  if (user.id === currentUser.id) return null
  if (invitationState === 'unknown') return null

  // A revoked invitation is not a deactivated member. Restoring it always
  // submits a fresh email before re-enabling the account.
  if (invitationState === 'revoked') {
    return (
      <button onClick={() => !readOnly && onRestoreInvite(user)} disabled={readOnly}
        title={readOnly ? READ_ONLY_MESSAGE : 'Restore this invitation and send a new email'}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium whitespace-nowrap text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <RotateCcw className="w-3 h-3" /> Restore invite
      </button>
    )
  }

  // Only an accepted member can be reactivated.
  if (invitationState === 'deactivated') {
    return (
      <button onClick={() => !readOnly && onReactivate(user)} disabled={readOnly}
        title={readOnly ? READ_ONLY_MESSAGE : undefined}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <Check className="w-3.5 h-3.5" /> Reactivate
      </button>
    )
  }

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      if (spaceBelow < 120) {
        setMenuPos({ top: rect.top - 4, left: rect.right, openAbove: true })
      } else {
        setMenuPos({ top: rect.bottom + 4, left: rect.right, openAbove: false })
      }
    }
    setOpen(!open)
  }

  const isUnacceptedInvitation = ['pending', 'delivery_failed', 'expired', 'revoked'].includes(invitationState)
  const canRevokeInvitation = invitationState === 'pending'
  const canChangeInviteRole = invitationState !== 'revoked'
  const resendLabel = invitationState === 'delivery_failed'
    ? 'Retry Email'
    : invitationState === 'expired'
      ? 'Send New Invite'
      : 'Resend Invite'

  return (
    <>
      <button ref={btnRef} onClick={handleOpen}
        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-50" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1"
            style={{
              top: menuPos.openAbove ? undefined : menuPos.top,
              bottom: menuPos.openAbove ? (window.innerHeight - menuPos.top) : undefined,
              left: menuPos.left - 192,
            }}
          >
            {isUnacceptedInvitation ? (
              <>
              {canChangeInviteRole && (
                <>
                  <button onClick={() => { if (!readOnly) onChangeInviteRole(user); setOpen(false) }} disabled={readOnly}
                    title={readOnly ? READ_ONLY_MESSAGE : undefined}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed">
                    <UserCog className="w-4 h-4" /> Change Role
                  </button>
                  <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
                </>
              )}
              <button onClick={() => { if (!readOnly) onResendInvite(user); setOpen(false) }} disabled={readOnly}
                title={readOnly ? READ_ONLY_MESSAGE : undefined}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
                <Send className="w-4 h-4" /> {resendLabel}
              </button>
                {canRevokeInvitation && (
                  <>
                    <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
                    <button onClick={() => { if (!readOnly) onRevokeInvite(user); setOpen(false) }} disabled={readOnly}
                      title={readOnly ? READ_ONLY_MESSAGE : undefined}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
                      <X className="w-4 h-4" /> Revoke Invite
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button onClick={() => { if (!readOnly) onChangeRole(user); setOpen(false) }} disabled={readOnly}
                  title={readOnly ? READ_ONLY_MESSAGE : undefined}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed">
                  <UserCog className="w-4 h-4" /> Change Role
                </button>
                <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
                <button onClick={() => { if (!readOnly) onDeactivate(user); setOpen(false) }} disabled={readOnly}
                  title={readOnly ? READ_ONLY_MESSAGE : undefined}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed">
                  <X className="w-4 h-4" /> Deactivate
                </button>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  )
}


export default function Users({ activeTab = 'users', onTabChange, tabs = [] }) {
  const toast = useToast()
  // OpCo workspaces: the Admin role is global, so only OpCo admins grant or
  // remove it; invitees join the current workspace (none in All OpCos).
  const { enabled: opcoOn, isOpcoAdmin, opco: workspaceOpco } = useOpCo()
  // An Admin manages every user of their OpCo, other Admins included (the backend still
  // refuses an Admin in a second OpCo).
  const canManageAdmins = true
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [sortKey, setSortKey] = useState('name_asc')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkActionLoading, setBulkActionLoading] = useState(false)

  // Modal state
  const [confirmModal, setConfirmModal] = useState({ open: false })
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [roleModal, setRoleModal] = useState({ open: false, user: null, mode: 'member' })
  const [roleModalLoading, setRoleModalLoading] = useState(false)
  const [showAddUserModal, setShowAddUserModal] = useState(false)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [usersRes, meRes] = await Promise.all([
        adminApi.listUsers({ include_inactive: true }),
        authApi.getCurrentUser()
      ])
      setUsers(usersRes.data.users || [])
      setCurrentUser(meRes.data)
    } catch (err) {
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchQuery ||
      (u.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
    const invitationState = getInvitationState(u)
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && invitationState === 'active') ||
      (filterStatus === 'attention' && invitationState === 'delivery_failed') ||
      (filterStatus === 'invitations' && ['pending', 'delivery_failed', 'revoked', 'expired'].includes(invitationState)) ||
      (filterStatus === 'inactive' && invitationState === 'deactivated')
    return matchesSearch && matchesStatus
  })

  const activeCount = users.filter(u => getInvitationState(u) === 'active').length
  const pendingInvitesCount = users.filter(u => ['pending', 'delivery_failed'].includes(getInvitationState(u))).length
  const deliveryFailedCount = users.filter(u => getInvitationState(u) === 'delivery_failed').length
  const deactivatedCount = users.filter(u => getInvitationState(u) === 'deactivated').length
  const statCards = [
    {
      key: 'active',
      count: activeCount,
      label: 'Active Members',
      icon: UsersIcon,
      iconWrap: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      ring: 'ring-blue-200/80 dark:ring-blue-900/40',
      activeBg: 'bg-blue-50/60 dark:bg-blue-900/20',
    },
    {
      key: 'invitations',
      count: pendingInvitesCount,
      label: 'Open Invitations',
      icon: UserPlus,
      iconWrap: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      ring: 'ring-amber-200/80 dark:ring-amber-900/40',
      activeBg: 'bg-amber-50/60 dark:bg-amber-900/20',
    },
    {
      key: 'attention',
      count: deliveryFailedCount,
      label: 'Invites Needing Attention',
      icon: MailWarning,
      iconWrap: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-600 dark:text-red-400',
      ring: 'ring-red-200/80 dark:ring-red-900/40',
      activeBg: 'bg-red-50/60 dark:bg-red-900/20',
    },
    {
      key: 'inactive',
      count: deactivatedCount,
      label: 'Deactivated Members',
      icon: UsersIcon,
      iconWrap: 'bg-gray-100 dark:bg-gray-800',
      iconColor: 'text-gray-500 dark:text-gray-400',
      ring: 'ring-gray-200/80 dark:ring-gray-700/70',
      activeBg: 'bg-gray-50/80 dark:bg-gray-800/50',
    },
  ]
  const isReader = isReadOnlyUser(currentUser)

  // ─── Role Change ───
  const handleRoleConfirm = async (userId, newRole) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      setRoleModal({ open: false, user: null, mode: 'member' })
      return
    }
    try {
      setRoleModalLoading(true)
      if (roleModal.mode === 'invite') {
        // Two calls: set the role, then send a fresh invitation email.
        await adminApi.updateUserRole(userId, newRole)
        try {
          await adminApi.resendInvitation(userId)
          toast.success('Role updated and new invite sent')
        } catch (err) {
          toast.error(err.response?.data?.error || 'Role updated, but the new invite could not be sent')
        }
        loadData()
      } else {
        await adminApi.updateUserRole(userId, newRole)
        toast.success('Role updated')
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole.toLowerCase() } : u))
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role')
    } finally {
      setRoleModalLoading(false)
      setRoleModal({ open: false, user: null, mode: 'member' })
    }
  }

  // ─── Deactivate ───
  const handleDeactivateClick = (user) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    setConfirmModal({
      open: true,
      title: 'Deactivate User',
      message: `Are you sure you want to deactivate ${user.username || user.email}? They will lose access to the application and all templates.`
        + (opcoOn && !isOpcoAdmin ? ' If they also belong to another OpCo, they are only removed from this one.' : ''),
      confirmLabel: 'Deactivate',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          setConfirmLoading(true)
          const res = await adminApi.deactivateUser(user.id)
          // OpCo workspaces: a user who is also in another OpCo is only
          // removed from this one (an OpCo admin can deactivate them).
          if (res?.data?.removed_from_opco) {
            toast.success(`Removed from ${workspaceOpco?.name || res.data.removed_from_opco}`, res.data.message)
          } else {
            toast.success('User deactivated')
          }
          loadData()
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to deactivate user')
        } finally {
          setConfirmLoading(false)
          setConfirmModal({ open: false })
        }
      }
    })
  }

  // ─── Revoke pending invitation ───
  const handleRevokeInviteClick = (user) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    setConfirmModal({
      open: true,
      title: 'Revoke Invitation',
      message: `Revoke the invitation for ${user.username || user.email}? The email cannot be recalled, but its sign-in link will no longer grant access.`,
      confirmLabel: 'Revoke Invite',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          setConfirmLoading(true)
          await adminApi.revokeInvitation(user.id)
          toast.success('Invitation revoked. The sign-in link no longer grants access.')
          loadData()
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to revoke invitation')
        } finally {
          setConfirmLoading(false)
          setConfirmModal({ open: false })
        }
      }
    })
  }

  // ─── Resend pending invitation ───
  const handleResendInviteClick = (user) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    const invitationState = getInvitationState(user)
    const isRevoked = invitationState === 'revoked'
    setConfirmModal({
      open: true,
      title: invitationState === 'delivery_failed' ? 'Retry Invitation Email' : 'Resend Invitation',
      message: isRevoked
        ? `Send a fresh invitation email to ${user.username || user.email}?`
        : `Send a fresh invitation email to ${user.username || user.email}? The existing invitation remains valid unless you revoke it.`,
      confirmLabel: invitationState === 'delivery_failed' ? 'Retry Email' : 'Resend Invite',
      confirmVariant: 'default',
      onConfirm: async () => {
        try {
          setConfirmLoading(true)
          if (isRevoked) {
            await adminApi.restoreInvitation(user.id)
          } else {
            await adminApi.resendInvitation(user.id)
          }
          toast.success('Invite sent')
          loadData()
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to resend invitation')
        } finally {
          setConfirmLoading(false)
          setConfirmModal({ open: false })
        }
      }
    })
  }

  // ─── Reactivate ───
  const handleReactivateClick = (user) => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    setConfirmModal({
      open: true,
      title: 'Reactivate User',
      message: `Are you sure you want to reactivate ${user.username || user.email}? They will regain access to the application.`,
      confirmLabel: 'Reactivate',
      confirmVariant: 'default',
      onConfirm: async () => {
        try {
          setConfirmLoading(true)
          await adminApi.reactivateUser(user.id)
          toast.success('User reactivated')
          loadData()
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reactivate user')
        } finally {
          setConfirmLoading(false)
          setConfirmModal({ open: false })
        }
      }
    })
  }

  // ─── Bulk Actions ───
  const selectedUsers = useMemo(() => users.filter(u => selectedIds.has(u.id)), [users, selectedIds])

  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])

  const handleBulkDeactivate = () => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    const eligible = selectedUsers.filter(u => {
      const state = getInvitationState(u)
      return u.id !== currentUser?.id && state !== 'deactivated' && state !== 'revoked' && !['pending', 'delivery_failed', 'expired'].includes(state)
    })
    if (eligible.length === 0) {
      toast.info('No eligible users selected for deactivation')
      return
    }
    setConfirmModal({
      open: true,
      title: `Deactivate ${eligible.length} User${eligible.length > 1 ? 's' : ''}`,
      message: `Are you sure you want to deactivate ${eligible.length} selected user${eligible.length > 1 ? 's' : ''}? They will lose access to the application and all templates.`,
      confirmLabel: `Deactivate ${eligible.length}`,
      confirmVariant: 'danger',
      onConfirm: async () => {
        setBulkActionLoading(true)
        let succeeded = 0
        let failed = 0
        for (const user of eligible) {
          try {
            await adminApi.deactivateUser(user.id)
            succeeded++
          } catch {
            failed++
          }
        }
        if (succeeded > 0) toast.success(`${succeeded} user${succeeded > 1 ? 's' : ''} deactivated`)
        if (failed > 0) toast.error(`Failed to deactivate ${failed} user${failed > 1 ? 's' : ''}`)
        clearSelection()
        loadData()
        setBulkActionLoading(false)
        setConfirmModal({ open: false })
      }
    })
  }

  const handleBulkReactivate = () => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    const eligible = selectedUsers.filter(u => getInvitationState(u) === 'deactivated')
    if (eligible.length === 0) {
      toast.info('No deactivated users selected')
      return
    }
    setConfirmModal({
      open: true,
      title: `Reactivate ${eligible.length} User${eligible.length > 1 ? 's' : ''}`,
      message: `Are you sure you want to reactivate ${eligible.length} selected user${eligible.length > 1 ? 's' : ''}? They will regain access to the application.`,
      confirmLabel: `Reactivate ${eligible.length}`,
      confirmVariant: 'default',
      onConfirm: async () => {
        setBulkActionLoading(true)
        let succeeded = 0
        let failed = 0
        for (const user of eligible) {
          try {
            await adminApi.reactivateUser(user.id)
            succeeded++
          } catch {
            failed++
          }
        }
        if (succeeded > 0) toast.success(`${succeeded} user${succeeded > 1 ? 's' : ''} reactivated`)
        if (failed > 0) toast.error(`Failed to reactivate ${failed} user${failed > 1 ? 's' : ''}`)
        clearSelection()
        loadData()
        setBulkActionLoading(false)
        setConfirmModal({ open: false })
      }
    })
  }

  const handleBulkResendInvites = () => {
    if (isReader) {
      toast.error(READ_ONLY_MESSAGE)
      return
    }
    const eligible = selectedUsers.filter(u => {
      const state = getInvitationState(u)
      return ['pending', 'delivery_failed', 'expired', 'revoked'].includes(state)
    })
    if (eligible.length === 0) {
      toast.info('No pending invitations selected')
      return
    }
    setConfirmModal({
      open: true,
      title: `Resend ${eligible.length} Invitation${eligible.length > 1 ? 's' : ''}`,
      message: `Send fresh invitation emails to ${eligible.length} selected user${eligible.length > 1 ? 's' : ''}?`,
      confirmLabel: `Resend ${eligible.length}`,
      confirmVariant: 'default',
      onConfirm: async () => {
        setBulkActionLoading(true)
        let succeeded = 0
        let failed = 0
        for (const user of eligible) {
          try {
            const state = getInvitationState(user)
            if (state === 'revoked') {
              await adminApi.restoreInvitation(user.id)
            } else {
              await adminApi.resendInvitation(user.id)
            }
            succeeded++
          } catch {
            failed++
          }
        }
        if (succeeded > 0) toast.success(`${succeeded} invitation${succeeded > 1 ? 's' : ''} sent`)
        if (failed > 0) toast.error(`Failed to send ${failed} invitation${failed > 1 ? 's' : ''}`)
        clearSelection()
        loadData()
        setBulkActionLoading(false)
        setConfirmModal({ open: false })
      }
    })
  }

  // ─── Loading ───
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
          </div>
          <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    )
  }

  // ─── Non-Admin ───
  if (!currentUser || (currentUser.role !== 'admin' && !isReader)) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-8 text-center max-w-md mx-auto mt-12">
          <ShieldAlert className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300">Admin Access Required</h2>
          <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-2">You need admin privileges to manage users. Contact your organisation admin.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <UsersIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Users</h1>
              <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold text-navy-700 ring-1 ring-inset ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                {users.length}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage members, invitation delivery, roles, and organisation access.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tabs.length > 0 && typeof onTabChange === 'function' && (
            <div className="inline-flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1 shadow-sm ring-1 ring-gray-100 dark:ring-gray-800/80">
              {tabs.map(tab => {
                const isActive = activeTab === tab.key
                return (
                  <button
                    key={tab.key}
                    onClick={() => onTabChange(tab.key)}
                    className={`inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-navy-800 text-white shadow-sm ring-1 ring-navy-900/20'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    aria-pressed={isActive}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </div>
          )}
          <button
            onClick={() => setShowAddUserModal(true)}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-navy-800 text-white text-sm font-semibold shadow-sm ring-1 ring-navy-900/20 transition-all duration-200 hover:bg-navy-900 enabled:hover:-translate-y-0.5 enabled:hover:shadow-lg enabled:active:translate-y-0 enabled:active:scale-[0.98]"
          >
            <UserPlus className="w-4 h-4" />
            Invite User
          </button>
        </div>
      </div>

      {/* Stats (click to filter) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, idx) => {
          const isActive = filterStatus === card.key
          const Icon = card.icon
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => setFilterStatus(card.key)}
              aria-pressed={isActive}
              className={`group text-left bg-white dark:bg-gray-900 rounded-xl border p-5 shadow-sm ring-1 transition-all duration-200 animate-fade-in ${
                isActive
                  ? `${card.activeBg} border-transparent ${card.ring} shadow-md`
                  : 'border-gray-200 dark:border-gray-800 ring-transparent hover:-translate-y-0.5 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700'
              }`}
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105 ${card.iconWrap}`}>
                  <Icon className={`w-5 h-5 ${card.iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 stat-number-animate">{card.count}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{card.label}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden min-w-0">
        {/* Toolbar - all controls in single responsive row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40">
          {/* Search */}
          <div className="relative flex-1 min-w-0 sm:max-w-xs lg:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text" placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
            />
          </div>

          {/* Controls: Filters + Sort - right aligned on larger screens */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-auto">
            {/* Status Filters */}
            <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-800/70">
              {[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'attention', label: 'Attention' },
                { value: 'invitations', label: 'Invited' },
                { value: 'inactive', label: 'Inactive' },
              ].map(({ value, label }) => (
                <button key={value} onClick={() => setFilterStatus(value)}
                  className={`px-2 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                    filterStatus === value
                      ? 'bg-navy-800 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-1.5">
              <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <select
                value={sortKey}
                onChange={e => setSortKey(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-navy-500/20 min-w-0"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Sectionized Table */}
        <div className="p-0 sm:p-4">
          <SectionizedUserTable
            users={filteredUsers}
            currentUserId={currentUser?.id}
            sortable
            collapsible
            sectionCounts
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            externalSortKey={sortKey}
            onSortChange={setSortKey}
            tableMinWidthClass="min-w-[1120px]"
            actionsHeaderClassName="w-36 text-right"
            actionsCellClassName="text-right"
            renderBulkActions={(selected) => {
              const activeUsers = selected.filter(u => getInvitationState(u) === 'active')
              const deactivatedUsers = selected.filter(u => getInvitationState(u) === 'deactivated')
              const pendingInvites = selected.filter(u => ['pending', 'delivery_failed', 'expired', 'revoked'].includes(getInvitationState(u)))
              return (
                <>
                  {activeUsers.length > 0 && (
                    <button
                      onClick={handleBulkDeactivate}
                      disabled={bulkActionLoading || isReader}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                      Deactivate ({activeUsers.length})
                    </button>
                  )}
                  {deactivatedUsers.length > 0 && (
                    <button
                      onClick={handleBulkReactivate}
                      disabled={bulkActionLoading || isReader}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <UserCheck2 className="w-3.5 h-3.5" />
                      Reactivate ({deactivatedUsers.length})
                    </button>
                  )}
                  {pendingInvites.length > 0 && (
                    <button
                      onClick={handleBulkResendInvites}
                      disabled={bulkActionLoading || isReader}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Resend ({pendingInvites.length})
                    </button>
                  )}
                </>
              )
            }}
            columns={[
              {
                key: 'username',
                label: 'User',
                colClassName: 'w-[22%]',
                cellClassName: 'min-w-0',
                render: (user, { isSelf }) => (
                  <div className="flex items-center gap-3 min-w-0">
                    <UserAvatar user={user} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={user.username}>
                          {user.username}
                        </span>
                        {isSelf && <span className="text-[10px] text-gray-400 dark:text-gray-500 font-normal uppercase tracking-wide">you</span>}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'email',
                label: 'Email',
                colClassName: 'w-[20%]',
                cellClassName: 'hidden lg:table-cell min-w-0',
                headerClassName: 'hidden lg:table-cell',
                render: (user) => <span className="block text-sm text-gray-500 dark:text-gray-400 truncate" title={user.email}>{user.email}</span>,
              },
              {
                key: 'job_title',
                label: 'Job Title',
                colClassName: 'w-[12%]',
                cellClassName: 'hidden xl:table-cell min-w-0',
                headerClassName: 'hidden xl:table-cell',
                render: (user) => <span className="block text-sm text-gray-500 dark:text-gray-400 truncate" title={user.job_title || ''}>{user.job_title || ''}</span>,
              },
              {
                key: 'role',
                label: 'Role',
                colClassName: 'w-[12%]',
                render: (user) => {
                  const roleBadges = {
                    admin: { label: 'Admin', icon: ShieldCheck, bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
                    template_manager: { label: 'Template Manager', icon: FolderCog, bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400' },
                    sender: { label: 'Reviewer', icon: Send, bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
                    reviewer: { label: 'Approver', icon: ClipboardCheck, bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
                    user: { label: 'User', icon: UsersIcon, bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
                    reader: { label: 'Reader', icon: Eye, bg: 'bg-gray-100 dark:bg-gray-800/50', text: 'text-gray-600 dark:text-gray-400' },
                  }
                  const badge = roleBadges[user.role] || roleBadges.user
                  const Icon = badge.icon
                  // A working Super Admin is always an Admin: show only the Super Admin tag.
                  if (user.is_super_admin) return <SuperAdminTag />
                  return (
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${badge.bg} ${badge.text}`}>
                      <Icon className="w-3 h-3" /> {badge.label}
                    </span>
                  )
                },
              },
              {
                key: 'invitation_state',
                label: 'Status',
                colClassName: 'w-[16%]',
                render: (user) => <InvitationStatusBadge user={user} />,
              },
              {
                key: 'last_login',
                label: 'Last Login',
                colClassName: 'w-[10%]',
                cellClassName: 'hidden md:table-cell',
                headerClassName: 'hidden md:table-cell',
                render: (user) => (
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : <span className="text-gray-400 dark:text-gray-500">Never</span>}
                  </span>
                ),
              },
            ]}
            renderActions={(user) => (
              <UserActions user={user} currentUser={currentUser}
                readOnly={isReader}
                onChangeRole={(u) => setRoleModal({ open: true, user: u, mode: 'member' })}
                onChangeInviteRole={(u) => setRoleModal({ open: true, user: u, mode: 'invite' })}
                onDeactivate={handleDeactivateClick}
                onResendInvite={handleResendInviteClick}
                onRestoreInvite={handleResendInviteClick}
                onRevokeInvite={handleRevokeInviteClick}
                onReactivate={handleReactivateClick}
              />
            )}
          />
        </div>
      </div>

      {/* Modals */}
      <ConfirmModal
        open={confirmModal.open} title={confirmModal.title} message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel} confirmVariant={confirmModal.confirmVariant}
        loading={confirmLoading || bulkActionLoading} onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false })}
      />

      <RoleModal
        open={roleModal.open} user={roleModal.user} mode={roleModal.mode}
        loading={roleModalLoading}
        canManageAdmins={canManageAdmins}
        onClose={() => setRoleModal({ open: false, user: null, mode: 'member' })}
        onConfirm={handleRoleConfirm}
      />

      <AddUserModal
        open={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSuccess={loadData}
        canGrantAdmin={canManageAdmins}
        opcoName={opcoOn ? workspaceOpco?.name : null}
      />
    </div>
  )
}
